import { useState, FC, useCallback } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import {
  Typography,
  Button,
  Stack,
  Box,
  InputLabel,
  TextField as MuiTextField,
  IconButton,
  InputAdornment,
} from '@mui/material'
import Clear from '@mui/icons-material/Clear'
import { path } from 'ramda'
import { Field, useFormikContext } from 'formik'
import { TextField } from 'formik-mui'
import { FriendlyError } from '@cibo/ui'
import { AttachmentFileDescriptor } from '../types'

const DEFAULT_ACCEPTED_FILES = ['.csv', '.xls', '.xls', '.pdf', '.jpg', '.png', '.txt']
const DEFAULT_MAX_FILE_SIZE = 200 * 1024 * 1024

interface UploadFileFormProps {
  fileDescriptor?: AttachmentFileDescriptor
  name?: string
  uploadError?: string
}

export interface FileUploadFormValues {
  file: { file: File; name?: string }
}

export const UploadFileForm: FC<UploadFileFormProps> = ({
  uploadError,
  name = 'fileUpload',
  fileDescriptor,
}) => {
  const { t } = useTranslation('@cibo/landmanager/fileModal')
  const { setFieldValue, values } = useFormikContext<FileUploadFormValues>()
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  const onDrop = useCallback((acceptedFiles, rejectedFiles: FileRejection[]) => {
    if (acceptedFiles.length > 0) {
      setErrorMessage(undefined)
      setFieldValue(`${name}.file.file`, acceptedFiles[0])
    } else {
      setErrorMessage(t('rejectFile'))
      setFieldValue(`${name}.file.file`, undefined)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: DEFAULT_ACCEPTED_FILES,
    maxSize: DEFAULT_MAX_FILE_SIZE,
    ...fileDescriptor,
    multiple: false,
    noClick: true,
  })
  return (
    <div {...getRootProps()}>
      <Stack spacing={1}>
        <input {...getInputProps()} />
        {!!path([name, 'file', 'file'], values) ? (
          <>
            <Box>
              <InputLabel shrink htmlFor="file">
                {t('file')}
              </InputLabel>
              <MuiTextField
                value={path([name, 'file', 'file', 'name'], values)}
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('clearFile')}
                        onClick={() => setFieldValue(`${name}.file.file`, undefined)}
                        edge="end"
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box>
              <InputLabel shrink htmlFor="name">
                {t('renameFile')}
              </InputLabel>
              <Field
                id="name"
                name={`${name}.file.name`}
                component={TextField}
                value={path([name, 'file', 'name'], values)}
              />
            </Box>
          </>
        ) : (
          <Button onClick={open} variant="contained">
            {t('selectFile')}
          </Button>
        )}
        {(errorMessage || uploadError) && (
          <FriendlyError message={(errorMessage || uploadError) as string} />
        )}
      </Stack>

      {isDragActive && (
        <Box
          sx={{
            background: 'rgba(255,255,255,0.95)',
            zIndex: 20,
          }}
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          mt={1.5}
          alignItems="stretch"
          justifyContent="flex-end"
        >
          <Box
            alignSelf="stretch"
            m={3}
            mt={1.5}
            p={5}
            textAlign="center"
            sx={{ borderRadius: '8px', border: '3px gray dashed' }}
          >
            <Typography variant="h6">{t('dropFileHere')}</Typography>
          </Box>
        </Box>
      )}
    </div>
  )
}
